/**
 * Late IN / Early Out thresholds for team attendance.
 *
 * Default office hours: 09:30–18:00 with 15 min HR grace
 *   → Late IN after 09:45, Early Out before 17:45
 *
 * When a row/member carries shift start/end (various field names),
 * thresholds are start+grace / end−grace for that employee.
 */

export const DEFAULT_SHIFT_START_MINS = (9 * 60) + 30 // 09:30
export const DEFAULT_SHIFT_END_MINS = 18 * 60 // 18:00
export const SHIFT_GRACE_MINS = 15

const START_FIELD_KEYS = [
  'shift_start',
  'shiftStart',
  'office_start',
  'officeStart',
  'work_start',
  'workStart',
  'start_time',
  'startTime',
  'shift_start_time',
  'shiftStartTime',
  'expected_in',
  'expectedIn',
  'expected_start',
  'expectedStart',
]

const END_FIELD_KEYS = [
  'shift_end',
  'shiftEnd',
  'office_end',
  'officeEnd',
  'work_end',
  'workEnd',
  'end_time',
  'endTime',
  'shift_end_time',
  'shiftEndTime',
  'expected_out',
  'expectedOut',
  'expected_end',
  'expectedEnd',
]

/**
 * Parse "HH:mm", "H:mm:ss", minutes-number, or Date-like string → minutes from midnight.
 * @returns {number|null}
 */
export function parseTimeToMins(value) {
  if (value == null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value < 0 || value >= 24 * 60) return null
    return Math.floor(value)
  }
  const s = String(value).trim()
  if (!s || s === '-' || s === '--' || s === '—' || s === '–') return null
  // ISO / datetime → local wall-clock HH:mm via Date when possible
  if (s.includes('T') || /\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s)
    if (!Number.isNaN(d.getTime())) {
      return (d.getHours() * 60) + d.getMinutes()
    }
  }
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const mins = parseInt(m[2], 10)
  if (h < 0 || h > 23 || mins < 0 || mins > 59) return null
  return (h * 60) + mins
}

export function formatMinsAsTime(mins) {
  if (mins == null || !Number.isFinite(mins)) return ''
  const h = Math.floor(mins / 60) % 24
  const m = Math.abs(mins % 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function firstTruthyField(obj, keys) {
  if (!obj || typeof obj !== 'object') return null
  for (let i = 0; i < keys.length; i += 1) {
    const v = obj[keys[i]]
    if (v != null && v !== '') return v
  }
  return null
}

/**
 * Parse "08:00-17:00" / "8:00 – 17:00" / "08:00 to 17:00" style shift strings.
 * @returns {{ startMins: number|null, endMins: number|null }}
 */
function parseShiftTimingsString(raw) {
  if (raw == null || raw === '') return { startMins: null, endMins: null }
  if (typeof raw === 'object') {
    const startMins = parseTimeToMins(
      firstTruthyField(raw, ['start', 'from', 'in', ...START_FIELD_KEYS])
    )
    const endMins = parseTimeToMins(
      firstTruthyField(raw, ['end', 'to', 'out', ...END_FIELD_KEYS])
    )
    return { startMins, endMins }
  }
  const s = String(raw).trim()
  const parts = s.split(/\s*(?:-|–|—|to)\s*/i).filter(Boolean)
  if (parts.length < 2) return { startMins: null, endMins: null }
  return {
    startMins: parseTimeToMins(parts[0]),
    endMins: parseTimeToMins(parts[1]),
  }
}

/**
 * Resolve shift start/end minutes from a roster row and/or member/user object.
 * Falls back to company defaults (09:30 / 18:00).
 */
export function resolveShiftMins(source) {
  const sources = Array.isArray(source) ? source : [source]
  let startMins = null
  let endMins = null

  for (let i = 0; i < sources.length; i += 1) {
    const obj = sources[i]
    if (!obj || typeof obj !== 'object') continue

    if (startMins == null) {
      startMins = parseTimeToMins(firstTruthyField(obj, START_FIELD_KEYS))
    }
    if (endMins == null) {
      endMins = parseTimeToMins(firstTruthyField(obj, END_FIELD_KEYS))
    }

    if (startMins == null || endMins == null) {
      const timingRaw = firstTruthyField(obj, [
        'shift_timings',
        'shiftTimings',
        'shift_timing',
        'shiftTiming',
        'shift',
        'office_hours',
        'officeHours',
        'work_timings',
        'workTimings',
      ])
      if (timingRaw != null) {
        const parsed = parseShiftTimingsString(timingRaw)
        if (startMins == null) startMins = parsed.startMins
        if (endMins == null) endMins = parsed.endMins
      }
    }

    if (startMins != null && endMins != null) break
  }

  return {
    startMins: startMins != null ? startMins : DEFAULT_SHIFT_START_MINS,
    endMins: endMins != null ? endMins : DEFAULT_SHIFT_END_MINS,
    hasCustomShift: startMins != null || endMins != null,
  }
}

/** Late IN threshold = shift start + grace (exclusive: punch after this is late). */
export function getLateInThresholdMins(source) {
  const { startMins } = resolveShiftMins(source)
  return startMins + SHIFT_GRACE_MINS
}

/** Early Out threshold = shift end − grace (exclusive: punch before this is early). */
export function getEarlyOutThresholdMins(source) {
  const { endMins } = resolveShiftMins(source)
  return endMins - SHIFT_GRACE_MINS
}

export function isLateInPunch(punchInValue, source) {
  const mins = parseTimeToMins(punchInValue)
  if (mins == null) return false
  return mins > getLateInThresholdMins(source)
}

export function isEarlyOutPunch(punchOutValue, source) {
  const mins = parseTimeToMins(punchOutValue)
  if (mins == null) return false
  return mins < getEarlyOutThresholdMins(source)
}

/**
 * Pull known shift fields from a user/member object for attaching to roster rows.
 * Returns a sparse object (only defined keys) so defaults stay in the threshold helper.
 */
export function extractShiftFieldsFromMember(member) {
  if (!member || typeof member !== 'object') return {}
  const out = {}
  START_FIELD_KEYS.forEach((key) => {
    if (member[key] != null && member[key] !== '') out[key] = member[key]
  })
  END_FIELD_KEYS.forEach((key) => {
    if (member[key] != null && member[key] !== '') out[key] = member[key]
  })
  ;['shift_timings', 'shiftTimings', 'shift_timing', 'shiftTiming', 'shift', 'office_hours', 'officeHours', 'work_timings', 'workTimings']
    .forEach((key) => {
      if (member[key] != null && member[key] !== '') out[key] = member[key]
    })
  return out
}
